/** A person's initial on a colour that holds AA under white text (the mock's palette), and a file's kind tile. */
import { cn } from "@/lib/utils";

const PERSON_COLORS = [
  "#3172b1",
  "#327e3e",
  "#ba513b",
  "#976b00",
  "#ab3a66",
] as const;
const OWNER_COLOR = "#6e45ab";

export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1)
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PERSON_COLORS[h % PERSON_COLORS.length]!;
}

export function Avatar({
  name,
  seed,
  owner = false,
  size = 36,
}: {
  name: string;
  seed: string;
  owner?: boolean;
  size?: number;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid shrink-0 place-items-center rounded-full font-semibold text-white",
        size >= 32 ? "text-3" : "text-2.5"
      )}
      style={{
        width: size,
        height: size,
        background: owner ? OWNER_COLOR : avatarColor(seed),
      }}
    >
      {(name.trim()[0] ?? "?").toUpperCase()}
    </span>
  );
}

type Kind = "coral" | "blue" | "green" | "violet" | "amber";
function kindOf(name: string, mime?: string | null): Kind {
  const m = mime ?? "";
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (m === "application/pdf" || ext === "pdf") return "coral";
  if (
    m.startsWith("image/") ||
    m.startsWith("video/") ||
    m.startsWith("audio/")
  )
    return "blue";
  if (m === "text/html" || ext === "html" || ext === "htm") return "green";
  if (/sheet|excel|csv/.test(m) || ["xls", "xlsx", "csv"].includes(ext))
    return "violet";
  return "amber";
}

export function FileTile({
  name,
  mimeType,
  size = "md",
}: {
  name: string;
  mimeType?: string | null;
  size?: "sm" | "md";
}) {
  const kind = kindOf(name, mimeType);
  const ext = (name.includes(".") ? name.split(".").pop()! : "file")
    .slice(0, 4)
    .toUpperCase();
  return (
    <span
      aria-hidden
      className={cn(
        "grid shrink-0 place-items-center text-2.5 font-bold tracking-[.02em]",
        size === "md" ? "size-10 rounded-12" : "size-9 rounded-10"
      )}
      // The kind is only known at runtime. Tint and ink mix toward the theme's surface and
      // ink, so the tile reads right in dark mode too. The ink is 55% kind (the warn note's
      // mix): at least 4.5:1 on the tint for every kind in light mode (amber 4.8:1), where
      // 75% gave amber 3.1:1.
      style={{
        background: `color-mix(in srgb, var(--kind-${kind}) 14%, var(--bg-elevated))`,
        color: `color-mix(in srgb, var(--kind-${kind}) 55%, var(--fg-default))`,
      }}
    >
      {ext}
    </span>
  );
}
